import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const ConfirmContext = createContext(null);

/**
 * Provider + hook for a branded confirmation dialog.
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, confirmLabel, variant });
 */
export const ConfirmProvider = ({ children }) => {
    const [state, setState] = useState(null);
    const resolverRef = useRef(null);

    const confirm = useCallback((opts) => {
        setState({
            title: opts.title || "Are you sure?",
            message: opts.message || "",
            confirmLabel: opts.confirmLabel || "Confirm",
            cancelLabel: opts.cancelLabel || "Cancel",
            variant: opts.variant || "danger",
        });
        return new Promise((resolve) => {
            resolverRef.current = resolve;
        });
    }, []);

    const handle = (result) => {
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
        setState(null);
    };

    // Escape/Enter keyboard shortcuts
    useEffect(() => {
        if (!state) return;
        const onKey = (e) => {
            if (e.key === "Escape") handle(false);
            if (e.key === "Enter") handle(true);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-un-onyx/70 animate-slide-up"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => handle(false)}
                    data-testid="confirm-dialog-backdrop"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="un-card max-w-md w-full bg-white"
                        data-testid="confirm-dialog"
                    >
                        <div className="flex items-start justify-between border-b-2 border-un-onyx px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div
                                    className={`w-9 h-9 flex items-center justify-center border-2 border-un-onyx ${
                                        state.variant === "danger" ? "bg-un-berry text-white" : "bg-un-lime text-un-onyx"
                                    }`}
                                >
                                    <AlertTriangle className="w-4 h-4" />
                                </div>
                                <div className="un-label text-un-grape" data-testid="confirm-dialog-title">
                                    {state.title}
                                </div>
                            </div>
                            <button
                                onClick={() => handle(false)}
                                aria-label="Close"
                                className="un-btn-ghost !px-1 !py-1 text-un-onyx/60 hover:!text-un-berry"
                                data-testid="confirm-dialog-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="px-6 py-6 text-un-onyx text-base leading-relaxed" data-testid="confirm-dialog-message">
                            {state.message}
                        </div>
                        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-3">
                            <button
                                onClick={() => handle(false)}
                                className="un-btn-outline text-sm"
                                data-testid="confirm-dialog-cancel"
                            >
                                {state.cancelLabel}
                            </button>
                            <button
                                onClick={() => handle(true)}
                                className="un-btn-primary text-sm"
                                data-testid="confirm-dialog-confirm"
                                autoFocus
                            >
                                {state.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
    return ctx;
};
